import React, { useState, useEffect, useMemo } from 'react'
import {
  Upload,
  Table,
  Columns,
  CheckCircle,
  X,
  AlertCircle,
  ChevronRight,
  ChevronLeft,
  Building2,
  Check,
  Save,
  HelpCircle
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

type WizardStep = 'input' | 'mapping' | 'preview' | 'executing'

interface ColumnMapping {
  csvColumn: string
  stagingField: string
}

const STAGING_FIELDS = [
  { label: 'Unit Reference', value: 'unit_ref' },
  { label: 'Price (€)', value: 'unit_preco' },
  { label: 'Status', value: 'unit_estado' },
  { label: 'Typology', value: 'unit_tipologia' },
  { label: 'Floor', value: 'unit_piso' },
  { label: 'Fraction', value: 'unit_fracao' },
  { label: 'Private Area (m²)', value: 'unit_areaPrivativa' },
  { label: 'Exterior Area (m²)', value: 'unit_areaExterior' },
  { label: 'Total Area (m²)', value: 'unit_areaTotal' },
  { label: 'Parking', value: 'unit_estacionamento' },
  { label: 'Unit URL', value: 'unit_fracaoUrl' },
  { label: 'Project Name', value: 'project_name' },
]

interface DataWizardProps {
  isOpen: boolean
  onClose: () => void
  initialProjectId?: number
}

function SearchableProjectSelect({ 
  value, 
  onChange 
}: { 
  value: number | undefined, 
  onChange: (project: any) => void 
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects-search', searchTerm],
    queryFn: async () => {
      const response = await api.get(`/projects?search=${encodeURIComponent(searchTerm)}&limit=10`)
      return response.data.data || []
    },
    enabled: isOpen && searchTerm.length > 0,
  })

  return (
    <div className="relative">
      <div className="relative">
        <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search for a project to link these units..."
          className="form-input pl-10 pr-10"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setIsOpen(false); }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {isOpen && searchTerm.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-3 text-center text-secondary-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : projects && projects.length > 0 ? (
            projects.map((p: any) => (
              <button
                key={p.project_id}
                type="button"
                onClick={() => {
                  onChange(p)
                  setSearchTerm(p.name)
                  setIsOpen(false)
                }}
                className="w-full px-3 py-2 text-left hover:bg-secondary-50 focus:bg-secondary-50 focus:outline-none border-b border-secondary-100 last:border-0"
              >
                <div className="font-medium text-secondary-900">{p.name}</div>
                <div className="text-xs text-secondary-500">{p.city}, {p.district}</div>
              </button>
            ))
          ) : (
            <div className="p-3 text-secondary-500 text-center text-sm">
              No projects found
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function DataWizard({ isOpen, onClose, initialProjectId }: DataWizardProps) {
  const [step, setStep] = useState<WizardStep>('input')
  const [inputText, setInputText] = useState('')
  const [parsedData, setParsedData] = useState<any[]>([])
  const [columns, setColumns] = useState<string[]>([])
  const [mappings, setMappings] = useState<ColumnMapping[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(initialProjectId)
  const [selectedProject, setSelectedProject] = useState<any>(null)
  const [statusOverrides, setStatusOverrides] = useState<Record<number, string>>({})
  const [includeMissingAsSold, setIncludeMissingAsSold] = useState(false)
  
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // Fetch project details if initialProjectId is provided
  const { data: projectDetails } = useQuery({
    queryKey: ['projects', initialProjectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${initialProjectId}`)
      return response.data
    },
    enabled: !!initialProjectId && isOpen,
  })

  // Fetch existing units for comparison
  const { data: existingUnits } = useQuery({
    queryKey: ['project-units', selectedProjectId],
    queryFn: async () => {
      const response = await api.get(`/units?projectId=${selectedProjectId}&limit=1000`)
      // Backend returns { units: [], pagination: {} }
      return response.data.units || []
    },
    enabled: !!selectedProjectId && step === 'preview',
  })

  useEffect(() => {
    if (projectDetails) {
      setSelectedProject(projectDetails)
      setSelectedProjectId(projectDetails.project_id)
    }
  }, [projectDetails])

  // Step 1: Parse Input
  const handleParse = () => {
    if (!inputText.trim()) {
      toast.error('Please paste some data first')
      return
    }

    // Try to detect separator (Tab for Excel, or Semicolon/Comma for CSV)
    const lines = inputText.trim().split('\n')
    if (lines.length < 2) {
      toast.error('Data must contain at least a header and one row')
      return
    }

    const firstLine = lines[0]
    let separator = '\t' // Default to Tab (Excel)
    if (firstLine.includes('\t')) separator = '\t'
    else if (firstLine.includes(';')) separator = ';'
    else if (firstLine.includes(',')) separator = ','

    const headers = firstLine.split(separator).map(h => h.trim())
    const rows = lines.slice(1).map(line => {
      const values = line.split(separator).map(v => v.trim())
      const row: any = {}
      headers.forEach((h, i) => {
        row[h] = values[i] || ''
      })
      return row
    })

    setColumns(headers)
    setParsedData(rows)
    
    // Auto-map based on common names
    const initialMappings: ColumnMapping[] = []
    headers.forEach(header => {
      const lower = header.toLowerCase()
      let field = ''
      if (lower.includes('ref')) field = 'unit_ref'
      else if (lower.includes('price') || lower.includes('preço') || lower.includes('valor')) field = 'unit_preco'
      else if (lower.includes('status') || lower.includes('estado')) field = 'unit_estado'
      else if (lower.includes('typology') || lower.includes('tipologia') || lower.includes('t0') || lower.includes('t1')) field = 'unit_tipologia'
      else if (lower.includes('floor') || lower.includes('piso')) field = 'unit_piso'
      else if (lower.includes('fraction') || lower.includes('fração')) field = 'unit_fracao'
      else if (lower.includes('area') || lower.includes('área')) {
        if (lower.includes('priv') || lower.includes('int')) field = 'unit_areaPrivativa'
        else if (lower.includes('ext') || lower.includes('terrace')) field = 'unit_areaExterior'
        else field = 'unit_areaTotal'
      }
      else if (lower.includes('park') || lower.includes('estacionamento')) field = 'unit_estacionamento'
      else if (lower.includes('url') || lower.includes('link')) field = 'unit_fracaoUrl'
      else if (lower.includes('project') || lower.includes('projeto') || lower.includes('empreendimento')) field = 'project_name'

      if (field) {
        initialMappings.push({ csvColumn: header, stagingField: field })
      }
    })
    
    setMappings(initialMappings)
    setStep('mapping')
  }

  // Step 2: Mapping Logic
  const updateMapping = (csvColumn: string, stagingField: string) => {
    setMappings(prev => {
      const filtered = prev.filter(m => m.csvColumn !== csvColumn && m.stagingField !== stagingField)
      if (stagingField === 'none') return filtered
      return [...filtered, { csvColumn, stagingField }]
    })
  }

  // Step 3: Preview Data
  const previewData = useMemo(() => {
    const rows = parsedData.map((row, index) => {
      const mapped: any = { _id: index }
      mappings.forEach(m => {
        let value = row[m.csvColumn]
        if (m.stagingField.includes('preco') || m.stagingField.includes('area')) {
          let clean = value.toString().replace(/[^0-9,.-]/g, '')
          if (clean.includes(',') && clean.includes('.')) {
            clean = clean.replace(/\./g, '').replace(',', '.')
          } else if (clean.includes(',')) {
            clean = clean.replace(',', '.')
          } else if (clean.includes('.')) {
            const parts = clean.split('.')
            if (parts[parts.length - 1].length === 3) clean = clean.replace(/\./g, '')
          }
          value = parseFloat(clean) || 0
        }
        mapped[m.stagingField] = value
      })

      if (!mapped.unit_estado && (!mapped.unit_preco || mapped.unit_preco === 0)) {
        mapped.unit_estado = 'sold'
      } else if (!mapped.unit_estado) {
        mapped.unit_estado = 'available'
      }
      if (statusOverrides[index]) mapped.unit_estado = statusOverrides[index]

      // Diffing: Robust cross-matching (Reference or Fraction)
      if (existingUnits && existingUnits.length > 0) {
        const existing = existingUnits.find((u: any) => {
          const fileRef = String(mapped.unit_ref || '').trim().toLowerCase();
          const fileFrac = String(mapped.unit_fracao || '').trim().toLowerCase();
          const dbRef = String(u.unit_ref || '').trim().toLowerCase();
          const dbFrac = String(u.fraction || '').trim().toLowerCase();

          // Match if either file identifier matches either DB identifier
          const matchByRef = fileRef && (fileRef === dbRef || fileRef === dbFrac);
          const matchByFrac = fileFrac && (fileFrac === dbRef || fileFrac === dbFrac);
            
          return matchByRef || matchByFrac;
        })

        if (!existing) {
          mapped._diff = 'new'
        } else {
          // Compare price (handle nulls and decimals)
          const oldPrice = existing.current_price ? Math.round(Number(existing.current_price)) : 0
          const newPrice = mapped.unit_preco ? Math.round(Number(mapped.unit_preco)) : 0
          
          const hasPriceChange = newPrice !== 0 && newPrice !== oldPrice
          const hasStatusChange = mapped.unit_estado && mapped.unit_estado !== existing.status
          
          mapped._diff = (hasPriceChange || hasStatusChange) ? 'updated' : 'unchanged'
          mapped._existing = existing
        }
      } else if (existingUnits) {
        mapped._diff = 'new'
      }

      if (selectedProject) {
        mapped.project_name = selectedProject.name
        mapped.project_websiteUrl = selectedProject.website
      }
      return mapped
    })

    // Identify missing units using same robust logic
    let missing: any[] = []
    if (existingUnits && selectedProjectId) {
      missing = existingUnits.filter((u: any) => {
        const dbRef = String(u.unit_ref || '').trim().toLowerCase();
        const dbFrac = String(u.fraction || '').trim().toLowerCase();

        return !rows.some(r => {
          const fileRef = String(r.unit_ref || '').trim().toLowerCase();
          const fileFrac = String(r.unit_fracao || '').trim().toLowerCase();
          
          return (fileRef && (fileRef === dbRef || fileRef === dbFrac)) ||
                 (fileFrac && (fileFrac === dbRef || fileFrac === dbFrac));
        });
      }).map((u: any) => ({
        ...u,
        _diff: 'missing'
      }))
    }

    return { rows, missing }
  }, [parsedData, mappings, selectedProject, statusOverrides, existingUnits, selectedProjectId])

  const previewRows = previewData.rows
  const missingUnits = previewData.missing

  const hasStatusMapping = useMemo(() => 
    mappings.some(m => m.stagingField === 'unit_estado'), 
  [mappings])

  const hasProjectMapping = useMemo(() => 
    mappings.some(m => m.stagingField === 'project_name' || m.stagingField === 'project_websiteUrl'), 
  [mappings])

  const canProceedToPreview = useMemo(() => {
    if (selectedProjectId) return mappings.length > 0
    return hasProjectMapping
  }, [selectedProjectId, mappings, hasProjectMapping])

  const importMutation = useMutation({
    mutationFn: async (rows: any[]) => {
      const response = await api.post('/automation/import', { rows })
      return response.data
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Import successful!')
      queryClient.invalidateQueries({ queryKey: ['pipeline-status'] })
      onClose()
      navigate('/automation')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Import failed')
      setStep('preview')
    },
  })

  const handleImport = async () => {
    setStep('executing')

    // Auto-backup existing data before applying changes
    try {
      if (selectedProjectId) {
        toast.loading('Downloading backup of existing units...', { id: 'backup-loading' })
        const response = await api.get(`/export/units/csv?projectId=${selectedProjectId}`, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        const timestamp = new Date().toISOString().split('T')[0]
        const projectName = selectedProject?.name?.replace(/[^a-z0-9]/gi, '_') || 'project'

        link.href = url
        link.setAttribute('download', `backup_${projectName}_units_${timestamp}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        toast.success('Backup downloaded successfully', { id: 'backup-loading' })
      }
    } catch (error) {
      console.error('Backup failed:', error)
      toast.error('Warning: Pre-import backup failed. Proceeding with import anyway.', { id: 'backup-loading' })
    }

    // Strip internal properties (starting with _) before sending to API
    const cleanRows = previewRows.map(row => {
      const cleanRow: any = {};
      Object.keys(row).forEach(key => {
        if (!key.startsWith('_')) {
          cleanRow[key] = row[key];
        }
      });
      return cleanRow;
    });

    importMutation.mutate([
      ...cleanRows,
      ...(includeMissingAsSold ? missingUnits.map(u => ({
        unit_ref: u.unit_ref,
        unit_preco: u.current_price,
        unit_estado: 'sold',
        unit_tipologia: u.typology,
        unit_piso: u.floor,
        unit_fracao: u.fraction,
        unit_areaPrivativa: u.private_gpa,
        unit_areaExterior: u.extra_gpa,
        unit_areaTotal: u.total_gpa,
        unit_estacionamento: u.parking,
        unit_fracaoUrl: u.unit_url,
        project_name: selectedProject?.name,
        project_websiteUrl: selectedProject?.website
      })) : [])
    ])
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
      
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-tagus-lg w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Table className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Manual Data Wizard</h2>
                  <p className="text-primary-100 text-sm">Update unit status manually by pasting data</p>
                </div>
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Stepper */}
          <div className="bg-secondary-50 border-b border-secondary-200 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-center space-x-4 sm:space-x-8">
              {[
                { id: 'input', label: '1. Paste Data', icon: Upload },
                { id: 'mapping', label: '2. Map Columns', icon: Columns },
                { id: 'preview', label: '3. Preview', icon: CheckCircle },
              ].map((s, idx) => (
                <div key={s.id} className="flex items-center">
                  <div className={`flex items-center space-x-2 ${
                    step === s.id ? 'text-primary-600 font-semibold' : 
                    idx < ['input', 'mapping', 'preview'].indexOf(step) ? 'text-green-600' : 'text-secondary-400'
                  }`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs border-2 ${
                      step === s.id ? 'border-primary-600 bg-primary-50' : 
                      idx < ['input', 'mapping', 'preview'].indexOf(step) ? 'border-green-600 bg-green-50' : 'border-secondary-300'
                    }`}>
                      {idx < ['input', 'mapping', 'preview'].indexOf(step) ? <Check className="h-3 w-3" /> : idx + 1}
                    </div>
                    <span className="hidden sm:inline text-sm">{s.label}</span>
                  </div>
                  {idx < 2 && <ChevronRight className="h-4 w-4 ml-4 sm:ml-8 text-secondary-300" />}
                </div>
              ))}
            </div>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-6">
            
            {step === 'input' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                  <HelpCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-semibold mb-1">How to use:</p>
                    <ol className="list-decimal ml-4 space-y-1">
                      <li>Open your Excel file or the broker's website table.</li>
                      <li>Select the header row and the data rows you want to import.</li>
                      <li>Copy (Ctrl+C) and paste (Ctrl+V) into the box below.</li>
                    </ol>
                  </div>
                </div>

                {selectedProject ? (
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-secondary-400 mr-2" />
                      <div>
                        <span className="text-xs text-secondary-500 uppercase font-semibold">Target Project</span>
                        <div className="font-medium text-secondary-900">{selectedProject.name}</div>
                      </div>
                    </div>
                    {!initialProjectId && (
                      <button 
                        onClick={() => { setSelectedProject(null); setSelectedProjectId(undefined); }}
                        className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                      >
                        Change
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="form-label">Link to Project (Optional for Multi-Project)</label>
                    <SearchableProjectSelect 
                      value={selectedProjectId} 
                      onChange={(p) => {
                        setSelectedProject(p)
                        setSelectedProjectId(p.project_id)
                      }} 
                    />
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
                      <p className="font-semibold mb-1">💡 Multi-Project Import:</p>
                      If you leave this empty, you <strong>must</strong> include and map a "Project Name" or "URL" column in your data so we know where each unit belongs.
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="form-label">Paste Table Data Here</label>
                  <textarea
                    className="form-input font-mono text-sm h-64 resize-none"
                    placeholder="Reference	Price	Status&#10;REF123	500.000	Available&#10;REF456	650.000	Reserved"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                  />
                </div>
              </div>
            )}

              {step === 'mapping' && (
                <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <div className="flex items-center justify-between">
                    <p className="text-secondary-600 text-sm">
                      We found {columns.length} columns. Please map them to our system fields.
                    </p>
                    {!selectedProjectId && !hasProjectMapping && (
                      <div className="text-xs font-semibold text-danger-600 animate-pulse bg-danger-50 px-2 py-1 rounded border border-danger-100">
                        ⚠️ Please map "Project Name" or "Unit URL"
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {columns.map(col => {
                    const currentMapping = mappings.find(m => m.csvColumn === col)?.stagingField || 'none'
                    return (
                      <div key={col} className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex-1 truncate mr-4">
                          <span className="text-xs text-secondary-500 block mb-1">Source Column</span>
                          <span className="font-medium text-secondary-900 truncate" title={col}>{col}</span>
                        </div>
                        <div className="w-48">
                          <select
                            className="form-input text-sm"
                            value={currentMapping}
                            onChange={(e) => updateMapping(col, e.target.value)}
                          >
                            <option value="none">Ignore Column</option>
                            {STAGING_FIELDS.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="font-semibold text-secondary-900">Preview ({previewRows.length} units)</h3>
                      <p className="text-xs text-secondary-500">Review and refine unit statuses before importing.</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[10px] font-bold uppercase">
                        {previewRows.filter(r => r._diff === 'new').length} New
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold uppercase">
                        {previewRows.filter(r => r._diff === 'updated').length} Updated
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-600 text-[10px] font-bold uppercase">
                        {previewRows.filter(r => r._diff === 'unchanged').length} No Change
                      </span>
                    </div>
                  </div>
                  
                  {!hasStatusMapping && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 flex items-center space-x-2 text-amber-700 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span><strong>Status column not mapped.</strong> System automatically set units with 0 price as "Sold".</span>
                    </div>
                  )}
                </div>

                <div className="border border-secondary-200 rounded-lg overflow-hidden overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-50 sticky top-0 z-10">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider w-16">
                          Type
                        </th>
                        {mappings.filter(m => m.stagingField !== 'unit_estado').map(m => (
                          <th key={m.stagingField} className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">
                            {STAGING_FIELDS.find(f => f.value === m.stagingField)?.label}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider bg-primary-50/50">
                          Status / Sold?
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-200">
                      {previewRows.slice(0, 50).map((row, i) => (
                        <tr key={i} className={`hover:bg-secondary-50 ${row._diff === 'new' ? 'bg-green-50/30' : row._diff === 'updated' ? 'bg-blue-50/30' : ''}`}>
                          <td className="px-4 py-2">
                            {row._diff === 'new' ? (
                              <span className="text-green-600 font-bold text-[10px] uppercase">New</span>
                            ) : row._diff === 'updated' ? (
                              <span className="text-blue-600 font-bold text-[10px] uppercase">Update</span>
                            ) : (
                              <span className="text-secondary-400 font-bold text-[10px] uppercase">-</span>
                            )}
                          </td>
                          {mappings.filter(m => m.stagingField !== 'unit_estado').map(m => (
                            <td key={m.stagingField} className="px-4 py-2 text-sm text-secondary-900">
                              {m.stagingField === 'unit_preco' ? (
                                <div className="flex flex-col">
                                  <span className="font-mono font-medium">{Number(row[m.stagingField])} €</span>
                                  {row._diff === 'updated' && row._existing?.current_price && Number(row[m.stagingField]) !== Number(row._existing.current_price) && (
                                    <span className="text-[10px] text-secondary-400 line-through">{Number(row._existing.current_price).toLocaleString()} €</span>
                                  )}
                                </div>
                              ) : row[m.stagingField]?.toString()}
                            </td>
                          ))}
                          {/* Status interactive cell */}
                          <td className="px-4 py-2 text-sm bg-primary-50/20">
                            <div className="flex items-center space-x-3">
                              <select 
                                className="text-xs border-secondary-300 rounded focus:ring-primary-500 focus:border-primary-500 py-1 pl-2 pr-8"
                                value={row.unit_estado}
                                onChange={(e) => setStatusOverrides(prev => ({ ...prev, [i]: e.target.value }))}
                              >
                                <option value="available">Available</option>
                                <option value="reserved">Reserved</option>
                                <option value="sold">Sold</option>
                              </select>
                              <label className="flex items-center cursor-pointer group" title="Mark as Sold">
                                <input 
                                  type="checkbox" 
                                  className="h-4 w-4 text-primary-600 rounded border-secondary-300 focus:ring-primary-500"
                                  checked={row.unit_estado === 'sold'}
                                  onChange={(e) => setStatusOverrides(prev => ({ ...prev, [i]: e.target.checked ? 'sold' : 'available' }))}
                                />
                                <span className={`ml-2 text-xs font-medium ${row.unit_estado === 'sold' ? 'text-primary-700' : 'text-secondary-400 group-hover:text-secondary-600'}`}>
                                  Sold
                                </span>
                              </label>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {missingUnits.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-amber-700">
                      <AlertCircle className="h-4 w-4" />
                      <h4 className="font-semibold text-sm">Units Missing from Import ({missingUnits.length})</h4>
                    </div>
                    <p className="text-xs text-secondary-500">
                      These units exist in our database for this project but were not found in your pasted data. 
                      They might have been removed or sold. <strong>We recommend importing them as "Sold" to keep your database accurate.</strong>
                    </p>
                    <div className="border border-amber-100 rounded-lg bg-amber-50/30 overflow-hidden max-h-40 overflow-y-auto">
                      <table className="min-w-full divide-y divide-amber-100">
                        <thead className="bg-amber-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-amber-600 uppercase">Ref</th>
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-amber-600 uppercase">Current Status</th>
                            <th className="px-4 py-2 text-left text-[10px] font-bold text-amber-600 uppercase">Last Price</th>
                            <th className="px-4 py-2 text-right">
                              <button 
                                onClick={() => setIncludeMissingAsSold(!includeMissingAsSold)}
                                className={`text-[10px] font-bold uppercase px-2 py-1 rounded border transition-colors ${
                                  includeMissingAsSold 
                                  ? 'bg-primary-600 text-white border-primary-600' 
                                  : 'bg-white text-primary-600 border-primary-200 hover:bg-primary-50'
                                }`}
                              >
                                {includeMissingAsSold ? '✓ Marking as Sold' : 'Mark All as Sold'}
                              </button>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-amber-100">
                          {missingUnits.map(u => (
                            <tr key={u.unit_id} className={`text-xs ${includeMissingAsSold ? 'bg-primary-50/30 text-primary-900' : 'text-amber-800'}`}>
                              <td className="px-4 py-1.5 font-medium">{u.unit_ref || u.fraction}</td>
                              <td className="px-4 py-1.5 capitalize">
                                {includeMissingAsSold ? (
                                  <span className="flex items-center text-primary-700 font-bold">
                                    <Check className="h-3 w-3 mr-1" /> Sold
                                  </span>
                                ) : u.status}
                              </td>
                              <td className="px-4 py-1.5">{Number(u.current_price).toLocaleString()} €</td>
                              <td className="px-4 py-1.5 text-right">
                                <span className="text-[10px] italic opacity-60">Not in import</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'executing' && (
              <div className="h-64 flex flex-col items-center justify-center space-y-4 animate-in fade-in duration-300">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                <p className="text-secondary-600 font-medium">Processing your data...</p>
                <p className="text-secondary-400 text-sm">Please do not close this window.</p>
              </div>
            )}

          </div>

          {/* Footer Actions */}
          <div className="bg-secondary-50 border-t border-secondary-200 px-6 py-4 flex-shrink-0 flex items-center justify-between">
            <button
              onClick={onClose}
              className="btn-secondary text-sm"
              disabled={step === 'executing'}
            >
              Cancel
            </button>

            <div className="flex space-x-3">
              {step === 'mapping' && (
                <button onClick={() => setStep('input')} className="btn-secondary text-sm flex items-center">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </button>
              )}
              {step === 'preview' && (
                <button onClick={() => setStep('mapping')} className="btn-secondary text-sm flex items-center">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </button>
              )}

              {step === 'input' && (
                <button
                  onClick={handleParse}
                  className="btn-primary text-sm flex items-center"
                  disabled={!inputText.trim()}
                >
                  Continue to Mapping <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
              {step === 'mapping' && (
                <button
                  onClick={() => setStep('preview')}
                  className="btn-primary text-sm flex items-center"
                  disabled={!canProceedToPreview}
                >
                  Preview Data <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              )}
              {step === 'preview' && (
                <button
                  onClick={handleImport}
                  className="btn-primary text-sm bg-green-600 hover:bg-green-700 border-green-600 flex items-center"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Import {previewRows.length + (includeMissingAsSold ? missingUnits.length : 0)} Units
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
